import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { geoCentroid } from "d3-geo";
import type { GlobeMethods } from "react-globe.gl";
import type { MeshPhongMaterial } from "three";
import { feature } from "topojson-client";
import worldAtlas from "world-atlas/countries-110m.json";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import type { RankedEntry, Slot } from "../types";

type CountryFeature = Feature<Geometry, { name?: string }> & {
  id?: string | number;
  slot?: Slot;
  center: [number, number];
};

type WebsiteMarker = {
  country: CountryFeature;
  entry: RankedEntry;
  lat: number;
  lng: number;
  faviconUrl: string | null;
  selected: boolean;
};

const Globe = lazy(() => import("react-globe.gl"));

interface EarthGlobeProps {
  slots: Slot[];
  selectedSlot?: Slot;
  onSelect: (slot: Slot) => void;
}

function leader(slot?: Slot): RankedEntry | undefined {
  return [...(slot?.entries ?? [])].sort(
    (a, b) => a.rank - b.rank || b.amountCents - a.amountCents,
  )[0];
}

function faviconUrl(rawUrl: string): string | null {
  try {
    const website = new URL(/^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`);
    const favicon = new URL("/favicon.ico", website.origin);
    favicon.searchParams.set("outbid", "1");
    return favicon.toString();
  } catch {
    return null;
  }
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

export default function EarthGlobe({ slots, selectedSlot, onSelect }: EarthGlobeProps) {
  const globeRef = useRef<GlobeMethods>();
  const stageRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 720, height: 560 });
  const [globeMaterial, setGlobeMaterial] = useState<MeshPhongMaterial | null>(null);
  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const countries = useMemo(() => {
    const slotById = new Map(
      slots.map((slot) => {
        const rawId = slot.id.replace("country-", "");
        return [String(Number(rawId) || rawId), slot];
      }),
    );
    const collection = feature(
      worldAtlas as never,
      worldAtlas.objects.countries as never,
    ) as unknown as FeatureCollection<Geometry, { name?: string }>;

    return collection.features.map((country) => ({
      ...country,
      slot: slotById.get(String(Number(country.id) || country.id)),
      center: geoCentroid(country) as [number, number],
    })) as CountryFeature[];
  }, [slots]);

  useEffect(() => {
    let active = true;
    let material: MeshPhongMaterial | undefined;
    void import("three").then(({ MeshPhongMaterial }) => {
      material = new MeshPhongMaterial({
        color: "#eee7e0",
        emissive: "#ddd2ca",
        emissiveIntensity: 0.18,
        shininess: 3,
        specular: "#fffdfa",
      });
      if (active) setGlobeMaterial(material);
      else material.dispose();
    });
    return () => {
      active = false;
      material?.dispose();
    };
  }, []);

  const selectedCountry = useMemo(
    () => countries.find((country) => country.slot?.id === selectedSlot?.id),
    [countries, selectedSlot?.id],
  );

  const websiteMarkers = useMemo<WebsiteMarker[]>(
    () =>
      countries.flatMap((country) => {
        const entry = leader(country.slot);
        if (!entry) return [];
        return [{
          country,
          entry,
          lat: country.center[1],
          lng: country.center[0],
          faviconUrl: faviconUrl(entry.url),
          selected: country === selectedCountry,
        }];
      }),
    [countries, selectedCountry],
  );

  useEffect(() => {
    if (!stageRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setSize({
        width: Math.max(300, Math.floor(entry.contentRect.width)),
        height: Math.max(320, Math.floor(entry.contentRect.height)),
      });
    });
    observer.observe(stageRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!selectedCountry || !globeRef.current) return;
    globeRef.current.pointOfView(
      {
        lat: selectedCountry.center[1] - 6,
        lng: selectedCountry.center[0],
        altitude: 1.8,
      },
      reducedMotion ? 0 : 900,
    );
  }, [reducedMotion, selectedCountry]);

  useEffect(
    () => () => {
      document.body.style.cursor = "";
    },
    [],
  );

  function configureGlobe() {
    if (!globeRef.current) return;
    globeRef.current.renderer().setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    const controls = globeRef.current.controls();
    controls.autoRotate = false;
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.minDistance = 165;
    controls.maxDistance = 390;
    if (selectedCountry) {
      globeRef.current.pointOfView(
        {
          lat: selectedCountry.center[1] - 6,
          lng: selectedCountry.center[0],
          altitude: 1.8,
        },
        0,
      );
    }
  }

  const makeWebsiteMarker = useCallback((datum: object): HTMLElement => {
    const marker = datum as WebsiteMarker;
    const element = document.createElement("button");
    element.type = "button";
    element.className = `earth-website-marker${marker.selected ? " is-selected" : ""}`;
    element.title = `${marker.entry.name} — ${marker.country.properties.name ?? marker.country.slot?.label ?? "Country"}`;
    element.setAttribute("aria-label", element.title);
    element.style.pointerEvents = "auto";

    const fallback = document.createElement("span");
    fallback.textContent = marker.entry.name || "Website";
    element.append(fallback);

    if (marker.faviconUrl) {
      const icon = document.createElement("img");
      icon.alt = "";
      icon.width = 28;
      icon.height = 28;
      icon.addEventListener("load", () => element.classList.add("has-icon"));
      icon.addEventListener("error", () => icon.remove());
      element.prepend(icon);
      let attempts = 0;
      const loadWhenMounted = () => {
        if (!element.isConnected && attempts < 60) {
          attempts += 1;
          requestAnimationFrame(loadWhenMounted);
          return;
        }
        if (!element.isConnected) return;
        icon.src = marker.faviconUrl!;
      };
      requestAnimationFrame(loadWhenMounted);
    }

    element.addEventListener("pointerdown", (event) => event.stopPropagation());
    element.addEventListener("click", (event) => {
      event.stopPropagation();
      if (marker.country.slot) onSelect(marker.country.slot);
    });
    return element;
  }, [onSelect]);

  return (
    <div className="earth-globe-stage" ref={stageRef}>
      <Suspense fallback={<div className="earth-globe-loading">Loading Earth…</div>}>
        {globeMaterial ? <Globe
          ref={globeRef}
          width={size.width}
          height={size.height}
          backgroundColor="rgba(0,0,0,0)"
          globeMaterial={globeMaterial}
          showAtmosphere
          atmosphereColor="#e57255"
          atmosphereAltitude={0.16}
          showGraticules={false}
          polygonsData={countries}
          polygonCapColor={(datum) => {
            const country = datum as CountryFeature;
            if (country === selectedCountry) return "#e57255";
            if (country.slot?.entries.length) return "#c98472";
            if (country.slot) return "#d9d0c8";
            return "#e7e1dc";
          }}
          polygonSideColor={() => "#9a8177"}
          polygonStrokeColor={() => "rgba(40, 38, 36, .2)"}
          polygonAltitude={(datum) => (datum === selectedCountry ? 0.012 : 0.003)}
          polygonLabel={(datum) => {
            const country = datum as CountryFeature;
            const entry = leader(country.slot);
            const countryName = escapeHtml(country.properties.name ?? "Territory");
            const status = entry ? escapeHtml(entry.name) : "Available";
            return `<div class="earth-globe-tooltip"><b>${countryName}</b><span>${status}</span></div>`;
          }}
          onPolygonHover={(datum) => {
            document.body.style.cursor = datum && (datum as CountryFeature).slot ? "pointer" : "grab";
          }}
          onPolygonClick={(datum) => {
            const slot = (datum as CountryFeature).slot;
            if (slot) onSelect(slot);
          }}
          htmlElementsData={websiteMarkers}
          htmlLat="lat"
          htmlLng="lng"
          htmlAltitude={(datum) => (datum as WebsiteMarker).selected ? 0.1 : 0.065}
          htmlElement={makeWebsiteMarker}
          htmlTransitionDuration={reducedMotion ? 0 : 500}
          onGlobeReady={configureGlobe}
        /> : <div className="earth-globe-loading">Loading Earth…</div>}
      </Suspense>
      <div className="earth-globe-instruction">Drag to rotate · Scroll to zoom</div>
    </div>
  );
}
